import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Camera, Trash2 } from 'lucide-react';
import UserAvatar from '../../assets/images/avatar2.png';
import UserProvider from '../../infra/provider/userProvider';
import '../../styles/components/_editprofile.scss';
import UserType from '../../domain/entities/valueObjects/userType';

const EditProfile: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userType, setUserType] = useState('');
    const [profileImage, setProfileImage] = useState(UserAvatar);
    const [isEditing, setIsEditing] = useState(false);
    const [originalUserName, setOriginalUserName] = useState('');
    const [originalProfileImage, setOriginalProfileImage] = useState(UserAvatar);
    const [error, setError] = useState('');

    const userProvider = UserProvider.getInstance();

    useEffect(() => {
        loadUserData();
    }, []);

    const loadUserData = async () => {
        try {
            setIsLoading(true);
            setError('');
            const user = await userProvider.getCurrentUser();
            const userType = new UserType(localStorage.getItem('userType') ?? '');

            if (user) {
                const newUserName = user.name || '';
                const newUserEmail = user.email || '';
                const newUserType = userType.getFormatted();

                // Atualizar estado com dados do backend
                setUserName(newUserName);
                setUserEmail(newUserEmail);
                setUserType(newUserType);
                setOriginalUserName(newUserName);

                // Tratar imagem do perfil
                if (user.image) {
                    if (user.image instanceof File) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                            const result = e.target?.result as string;
                            setProfileImage(result);
                            setOriginalProfileImage(result);
                        };
                        reader.readAsDataURL(user.image);
                    } else if (typeof user.image === 'string') {
                        setProfileImage(user.image);
                        setOriginalProfileImage(user.image);
                    } else {
                        setProfileImage(UserAvatar);
                        setOriginalProfileImage(UserAvatar);
                    }
                } else {
                    setProfileImage(UserAvatar);
                    setOriginalProfileImage(UserAvatar);
                }
            }
        } catch (error) {
            setError('Erro ao carregar dados do usuário');
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                setIsLoadingPhoto(true);
                setError('');
                console.log('Fazendo upload da foto:', file.name, file.size, file.type);

                // Upload da foto para o backend
                await userProvider.uploadProfilePhoto(file);
                console.log('Upload da foto realizado com sucesso');

                // Atualizar a imagem localmente
                const reader = new FileReader();
                reader.onload = (e) => {
                    const result = e.target?.result as string;
                    setProfileImage(result);
                    setOriginalProfileImage(result);

                    // Disparar evento para atualizar outros componentes
                    window.dispatchEvent(new CustomEvent('profileUpdated', {
                        detail: { userName, profileImage: result }
                    }));
                };
                reader.readAsDataURL(file);

            } catch (error) {
                console.error('Erro ao fazer upload da foto:', error);
                setError('Erro ao fazer upload da foto');
            } finally {
                setIsLoadingPhoto(false);
            }
        }
    };

    const handleDeletePhoto = async () => {
        try {
            setIsLoadingPhoto(true);
            setError('');

            await userProvider.deleteProfilePhoto();

            setProfileImage(UserAvatar);
            setOriginalProfileImage(UserAvatar);

            // Disparar evento para atualizar outros componentes
            window.dispatchEvent(new CustomEvent('profileUpdated', {
                detail: { userName, profileImage: UserAvatar }
            }));

        } catch (error) {
            console.error('Erro ao deletar foto:', error);
            setError('Erro ao deletar foto');
        } finally {
            setIsLoadingPhoto(false);
        }
    };

    const handleSave = async () => {
        if (!userName.trim()) {
            setError('Nome é obrigatório');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Atualizar dados no backend
            await userProvider.updateCurrentUser({ name: userName });

            // Atualizar estado original
            setOriginalUserName(userName);
            setOriginalProfileImage(profileImage);

            // Disparar evento para atualizar outros componentes
            window.dispatchEvent(new CustomEvent('profileUpdated', {
                detail: { userName, profileImage }
            }));

            setIsEditing(false);
        } catch (error) {
            console.error('Erro ao salvar perfil:', error);
            setError('Erro ao salvar alterações');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setUserName(originalUserName);
        setProfileImage(originalProfileImage);
        setError('');
        setIsEditing(false);
    };

    if (isLoading) {
        return (
            <div className="edit-profile-container">
                <div className="loading-container">
                    <div className="loading-spinner" />
                    <p>Carregando dados do perfil...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="edit-profile-container">
            <div className="edit-profile-header">
                <h1>Editar perfil</h1>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            <div className="edit-profile-content">
                <div className="profile-section">
                    <div className="profile-image-container">
                        <div className="profile-image-wrapper">
                            <img
                                src={profileImage}
                                alt="Foto do perfil"
                                className="profile-image"
                            />
                            {isEditing && (
                                <>
                                    <label className="camera-overlay" htmlFor="image-upload">
                                        <Camera size={20} />
                                        <span>Alterar foto</span>
                                    </label>
                                    {profileImage !== UserAvatar && (
                                        <button
                                            className="delete-photo-btn"
                                            onClick={handleDeletePhoto}
                                            disabled={isLoadingPhoto}
                                            title="Deletar foto"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        <input
                            id="image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div className="profile-info">
                        <div className="info-group">
                            <label htmlFor="user-name">Nome</label>
                            <div className="input-wrapper">
                                <User size={16} className="input-icon" />
                                <input
                                    id="user-name"
                                    type="text"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                    disabled={!isEditing}
                                    className={isEditing ? 'editing' : ''}
                                />
                            </div>
                        </div>

                        <div className="info-group">
                            <label>Email</label>
                            <div className="input-wrapper disabled">
                                <User size={16} className="input-icon" />
                                <input
                                    type="email"
                                    value={userEmail}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="info-group">
                            <label>Tipo de usuário</label>
                            <div className="input-wrapper disabled">
                                <User size={16} className="input-icon" />
                                <input
                                    type="text"
                                    value={userType}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="profile-actions">
                    {!isEditing ? (
                        <button
                            className="edit-button"
                            onClick={() => setIsEditing(true)}
                        >
                            <User size={16} />
                            Editar perfil
                        </button>
                    ) : (
                        <div className="action-buttons">
                            <button
                                className="cancel-button"
                                onClick={handleCancel}
                                disabled={isLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="save-button"
                                onClick={handleSave}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="loading-spinner" />
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Salvar
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditProfile; 